import type {FileListRefusal} from "api/apiErrorIds";
import type {FileMeta} from "api/transferApi";

/**
 * The upload init's rules on a transfer's FILE LIST, judged before the over-limit offer or checkout is shown
 * (kitecorp/transferify-ui#2930, api kitecorp/transferify-api-service#2144).
 *
 * On 2026-09-24 a buyer paid for two over-limit allowances and the upload init refused both sends, most likely for
 * holding more files than a transfer may. The quote and the checkout were priced by bytes alone, so nothing between
 * the selection and the payment looked at the list. Now the composer DECLARES three facts about its selection on
 * both calls, and the api refuses a selection the init would refuse, before any price is served or any payment
 * starts, under the rule's own `errorId`.
 *
 * Plain TypeScript: this module imports no React.
 */

/**
 * What the composer declares about its selection, under the names the api's quote and checkout read. Lengths are
 * UTF-16 code units, which is what JavaScript's `length` counts and what the api's `@Size` counts on the init.
 */
export interface FileListFacts {
	fileCount: number;
	longestFileNameLength: number;
	longestContentTypeLength: number;
}

/**
 * The facts of a selection, measured on its DECLARATIONS: the name and content type each file will carry on the
 * upload init (`core/upload/uploadContract.toFileMeta`), so the quote judges exactly the strings the init will.
 */
export function fileListFactsOf(declared: ReadonlyArray<Pick<FileMeta, "name" | "contentType">>): FileListFacts {
	let longestFileNameLength = 0;
	let longestContentTypeLength = 0;
	for (const file of declared) {
		longestFileNameLength = Math.max(longestFileNameLength, file.name.length);
		longestContentTypeLength = Math.max(longestContentTypeLength, file.contentType.length);
	}
	return {fileCount: declared.length, longestFileNameLength, longestContentTypeLength};
}

/**
 * The file-list rule this composer can judge on its own, or null: the FILE COUNT, against the limit the api
 * publishes on `/plan` as `maxFilesPerTransfer` (passed in, never restated here).
 *
 * Only the count, deliberately. The api publishes no figure for the name and content-type bounds, so a check of
 * them here would need a copied number, which is what kitecorp/transferify-ui#2930 forbids; the quote judges them
 * and answers with the rule's id instead (`api/apiErrorIds.fileListRefusalOf`). The count is the rule that bit
 * (a large folder), and judging it here is what lets a signed-out sender be told before being asked to sign in to
 * pay for a send that cannot be made.
 */
export function fileListRefusedLocally(facts: FileListFacts, maxFilesPerTransfer: number): FileListRefusal | null {
	return facts.fileCount > maxFilesPerTransfer ? "tooManyFiles" : null;
}

/**
 * Whether two declarations describe the same list, by the three facts the api judges. What decides whether a
 * refusal given for one declaration still speaks for the current selection: a refusal belongs to the list it was
 * given for, and a selection changed since is a new question.
 */
export function sameFileListFacts(declared: FileListFacts, current: FileListFacts): boolean {
	return declared.fileCount === current.fileCount
		&& declared.longestFileNameLength === current.longestFileNameLength
		&& declared.longestContentTypeLength === current.longestContentTypeLength;
}
